import React, { useState } from 'react';
import '../App.css';
import { Alert } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import axios from 'axios';

function PasswordChange() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const email = location?.state?.email || '';
  const userId = location?.state?.userId;
  const name = location?.state?.name || '';
  const phoneNumber = location?.state?.phone_number || '';
  const resetMethod = location?.state?.resetMethod || 'email';
  const loginAuthToken = `${process.env.REACT_APP_BASE_TOKEN}`;

  const autoLoginAfterPasswordReset = async () => {
    if (!email) {
      navigate('/login');
      return;
    }

    const loginResponse = await axios.post(
      'https://api.japaking.online/api/users/login',
      {
        userNameOrEmail: email,
        password: newPassword,
      },
      {
        headers: {
          Authorization: `Bearer ${loginAuthToken}`,
        },
      }
    );

    if (loginResponse?.data?.success && loginResponse?.data?.message?.token) {
      localStorage.setItem('AuthToken', loginResponse.data.message.token);
      window.location.href = '/';
      return;
    }

    navigate('/login');
  };

  const handlePasswordChange = async () => {
    setPasswordError('');

    if (newPassword.length < 4) {
      setPasswordError('Password must be at least 4 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      let response;

      if (resetMethod === 'birthdate') {
        if (!userId) {
          setPasswordError('User id missing. Please verify birth date again.');
          setLoading(false);
          return;
        }

        response = await axios.patch(
          'https://api.japaking.online/api/users/reset-password-by-birthdate',
          {
            userId,
            newPassword,
            confirmNewPassword: confirmPassword,
          },
          {
            headers: { 'Content-Type': 'application/json' },
          }
        );
      } else {
        response = await axios.patch(
          'https://api.japaking.online/api/users/forget-password',
          {
            userNameOrEmail: email,
            newPassword,
            confirmNewPassword: confirmPassword,
          },
          {
            headers: { Authorization: `Bearer ${process.env.REACT_APP_BASE_AUTHKEY}` },
          }
        );
      }

      if (response?.data?.success) {
        await autoLoginAfterPasswordReset();
      } else {
        setPasswordError('Failed to change password. Please try again.');
      }
    } catch (error) {
      setPasswordError(error?.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-v2-page">
      <div className="login-v2-card">
        <button type="button" className="login-v2-back" onClick={() => navigate(-1)}>
          Back
        </button>

        <h1 className="login-v2-title">Reset Password</h1>
        <p className="login-v2-subtitle">
          {resetMethod === 'birthdate' ? 'Set a new password using birth date verification' : 'Set a new password for your account'}
        </p>

        {resetMethod === 'birthdate' ? (
          <div className="login-v2-field-block">
            <label className="login-v2-label">Confirmation Details</label>
            <div className="login-v2-confirmation-box">
              <p className="login-v2-confirmation-row"><strong>Name:</strong> {name || '-'}</p>
              <p className="login-v2-confirmation-row"><strong>Email:</strong> {email || '-'}</p>
              <p className="login-v2-confirmation-row"><strong>Phone Number:</strong> {phoneNumber || '-'}</p>
            </div>
          </div>
        ) : (
          <div className="login-v2-field-block">
            <label className="login-v2-label">Email</label>
            <input className="login-v2-input" value={email} disabled />
          </div>
        )}

        <div className="login-v2-field-block">
          <label className="login-v2-label">New Password</label>
          <div className="login-v2-password-wrap">
            <input
              type={showNewPassword ? 'text' : 'password'}
              className="login-v2-input login-v2-password-input"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="off"
            />
            <button
              type="button"
              className="login-v2-visibility"
              onClick={() => setShowNewPassword((prev) => !prev)}
              aria-label={showNewPassword ? 'Hide password' : 'Show password'}
            >
              {showNewPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </button>
          </div>
        </div>

        <div className="login-v2-field-block">
          <label className="login-v2-label">Confirm New Password</label>
          <div className="login-v2-password-wrap">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              className="login-v2-input login-v2-password-input"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="off"
            />
            <button
              type="button"
              className="login-v2-visibility"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
            >
              {showConfirmPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </button>
          </div>
        </div>

        {passwordError && (
          <Alert variant="danger" className="mt-2">
            {passwordError}
          </Alert>
        )}

        <button
          className="login-v2-button"
          onClick={handlePasswordChange}
          disabled={loading || !newPassword || !confirmPassword}
        >
          {loading ? 'Saving...' : 'Save Password'}
        </button>
      </div>
    </div>
  );
}

export default PasswordChange;
